// Apps-launcher tile context menu (2026-07-30 per Nika "allow right click on
// those dropdown icons and allow to add on dashboard") — right-clicking a
// SearchAppTile opens the house ContextDropdown with Open / Open in side view
// / Add to dashboard / Pin to navbar. Same registered itemsFn/actionFn
// contract as navbarTabMenu.js; `appTile` rides the dropdown state so the
// rows know which tile was targeted.

// Direct-import contract (same as navSearchResultMenu.js): these are plain
// exports built for exactly this cross-menu reuse — the pin lane is the
// per-user pinnedCustom prefs, the same one drag-to-navbar commits through.
// Bare shared-library import, not a deep navbarTabMenu.js subpath — see
// app.js's identical fix note (tickets/catalog-fixall.md P0). Deferred-call
// wrappers, not a module-scope destructure — see app.js's circular-init note.
import _wsShared from '@symbo-ls/workspace-shared'
const shellNavbarTabPrefs = (...args) => _wsShared.functions.navbarTabPrefs(...args)
const shellToggleCustomNavbarPin = (...args) => _wsShared.functions.toggleCustomNavbarPin(...args)

// House app routes → their home-dashboard app-icon widget ids (the
// AppStudioWidget… family in pages/main.js — HOME_CATALOG's `app-*` set).
// Tiles whose route isn't here (module pages, admin surfaces, canvas views)
// simply don't offer the dashboard row — there is no widget to add.
const ROUTE_WIDGET = {
  '/studio': 'app-studio',
  // /tasks is the canonical route (tickets→tasks rename 2026-08-06); the
  // widget id stays the internal 'app-tickets'. '/tickets' = legacy alias.
  '/tasks': 'app-tickets',
  '/tickets': 'app-tickets',
  '/chat': 'app-chat',
  '/calendar': 'app-calendar',
  '/reminders': 'app-reminders',
  '/meet': 'app-meet',
  '/announcements': 'app-broadcast',
  // /notes is the canonical route (documents→notes rename 2026-08-06); the
  // widget id stays the internal 'app-documents'. '/documents' = legacy alias.
  '/notes': 'app-documents',
  '/documents': 'app-documents',
  '/agents': 'app-agents',
  '/team-and-agents': 'app-team',
  '/analytics': 'app-analytics',
  '/workspace-analytics': 'app-analytics',
  '/team-members': 'app-people',
  '/system-status': 'app-health',
  '/system': 'app-health'
}

export const dashboardWidgetForRoute = (route) => ROUTE_WIDGET[String(route || '')] || null

// Native app-launcher drags already carry the shared entity-ref MIME for
// navbar pinning. This additive marker is intentionally narrower: only routes
// which map to a real Home widget carry it, so the dashboard never turns an
// arbitrary page, admin surface, or extension into a phantom widget.
export const HOME_WIDGET_DRAG_MIME = 'application/x-symbols-home-widget'

// Called by SearchAppTile after it stamps the shared page-ref. Keeping this
// alongside `dashboardWidgetForRoute` makes the mapping the single source for
// context-menu, keyboard, and native drag placement.
export const stampLauncherDashboardDrag = (event, route) => {
  const widgetId = dashboardWidgetForRoute(route)
  const transfer = event?.dataTransfer
  if (!widgetId || !transfer) return null
  try {
    transfer.setData('text/x-home-widget-id', widgetId)
    transfer.setData(HOME_WIDGET_DRAG_MIME, '1')
    // Preserve the shared ref source's copy/link policy. Home asks for `copy`,
    // which is already valid for the existing page-ref drag.
    return widgetId
  } catch (_) {
    return null
  }
}

// Single source of truth for "is this Home widget genuinely showing on the
// live board right now" — mirrors pages/main.js's own private
// `_findItemById`/`_isHomeWidgetAlreadyVisible` (that pair can't be
// imported here: pages/ and functions/ never import each other, DOMQL
// components resolve siblings via `el.call` only). Reads the board's DOM
// directly rather than the `_homeHiddenWidgets` prefs map, because the
// map's "already visible, no state to flip" branch (an existing widget
// dragged a second time) never touches `_homeHiddenWidgets` at all — a
// caller keying off that map instead would wrongly treat an already-working
// drop as unresolved. Used by SearchAppTile.onDragend's drop-fallback
// (tickets/sonnet.md CODEX-V8 row 5, 2026-08-13) to detect whether
// GridContainer's own onDrop actually applied a drop the browser accepted.
// A module host owns an id whenever it exists, so it is asked first.
export const isHomeWidgetVisibleOnGrid = (id) => {
  if (!id || typeof document === 'undefined') return false
  const sel = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(String(id)) : String(id)
  const item =
    document.querySelector(`.home-grid > [data-module-widget][data-widget-id="${sel}"]`) ||
    document.querySelector(`.home-grid > [data-widget-id="${sel}"]`)
  if (!item) return false
  if (item.style && item.style.display === 'none') return false
  // `hide:` hides through a class, so the computed display is the answer.
  const view = item.ownerDocument && item.ownerDocument.defaultView
  if (view && view.getComputedStyle) return view.getComputedStyle(item).display !== 'none'
  return true
}

export const isLauncherDashboardDrag = (types) => {
  if (!types) return false
  return Array.from(types).includes(HOME_WIDGET_DRAG_MIME)
}

const _notifyDashboardPlacement = (el, payload) => {
  try {
    el.call('openNotification', payload)
  } catch (_) {
    // The app switcher can render on a minimal standalone surface where the
    // shell toast mount is not present. The navigation/add intent remains safe.
  }
}

// Shared keyboard/context-menu equivalent of dragging a launcher tile onto
// Home. The final duplicate check still lives at the live grid drop boundary;
// this early state check avoids needless route/persistence work on a settled
// dashboard while keeping a fresh boot safe.
export function addLauncherAppToDashboard(route) {
  const root = this?.state?.root
  const widgetId = dashboardWidgetForRoute(route)
  if (!root || !widgetId || typeof root.update !== 'function') {
    _notifyDashboardPlacement(this, {
      type: 'info',
      title: this.call('polyglot', 'shell.launcherTileMenu.notOnHome'),
      message: this.call('polyglot', 'shell.launcherTileMenu.notOnHomeBody')
    })
    return { status: 'unsupported', widgetId: null }
  }

  // On the board already? The board's composed hidden map answers once it has
  // resolved: `!!hidden[id]`, an ABSENT key being a default-visible widget
  // (the general default map carries keys for hidden ids only). Before the
  // board resolves nobody knows, and the live grid's own duplicate check
  // answers at the drop (`_consumeAddWidgetIntent`). Restated in the menu's
  // row below — every export here is serialized on its own (FA204).
  const hiddenMap = root._homeHiddenWidgets
  if (hiddenMap && typeof hiddenMap === 'object' && !hiddenMap[widgetId]) {
    _notifyDashboardPlacement(this, {
      type: 'info',
      title: this.call('polyglot', 'shell.launcherTileMenu.alreadyOnHome'),
      message: this.call('polyglot', 'shell.launcherTileMenu.alreadyOnHomeBody')
    })
    return { status: 'exists', widgetId }
  }

  // GridContainer consumes this only after GridStack is live, then routes
  // through its existing restore/persistence path. Set it before navigation so
  // the intent survives the route handoff.
  root.update({ _homeAddWidget: widgetId }, { preventFetch: true })
  try {
    this.call('closeDropdown', 'searchApps')
  } catch (_) {
    /* the action also works from a context menu outside the launcher layer */
  }
  try {
    this.router('/', this.getRoot())
  } catch (_) {
    /* already on Home or router unavailable — GridContainer sees the intent */
  }
  return { status: 'queued', widgetId }
}

// A custom, AI-built app (an "extension") vs a house app. Extension launcher
// tiles are stamped with `kind: 'extension'` + `projectId` where they're built
// (workspaceProjects.js), and they always live under the `/x/` route
// namespace. The route prefix is the FALLBACK leg, so a tile composed before
// the stamp landed (a stored settings.apps row that predates it, or a thin
// { route } pointer) still gets the Edit row — it just can't carry a
// projectId, which the action below resolves from the route instead.
export const isCustomAppTile = (tile) =>
  !!tile &&
  (tile.kind === 'extension' || !!tile.projectId || /^\/x\//.test(String(tile.route || '')))

// itemsFn — `el.call('searchAppTileMenuItems', root)` (this = the Menu el).
export const searchAppTileMenuItems = function (root) {
  const cm = root && root.contextDropdown
  if (!cm || !cm.open || !cm.appTile) return []
  const t = cm.appTile
  // i18n — every `label` here is a polyglot KEY. ContextDropdown's row
  // renders `el.call('polyglot', s.label || '')`, so a key resolves and a
  // non-key would pass through — these are all shipped chrome, so all keys.
  const items = [{ key: 'open', action: 'open', label: 'shell.launcherTileMenu.open' }]
  // Side view only where a split can actually happen — same classifier +
  // open-primary conditions as the navbar tab menu (navbarTabMenu.js).
  let fullPage = false
  try {
    fullPage = !!(this.call('isCanvasRoute', t.route) || this.call('isFullPageRoute', t.route))
  } catch (_) {
    fullPage = false
  }
  if (!fullPage && !!root._canvasSlot && root._canvasSlot !== t.route) {
    items.push({ key: 'sideview', action: 'sideview', label: 'shell.launcherTileMenu.sideView' })
  }
  // Custom, AI-built apps are authored in /add-app — one extension is one
  // thread there — so Edit takes you back to that app's own thread
  // (2026-08-02 per Nika "on custom made apps you can add context menu edit").
  // House apps have no authoring surface and get no Edit row.
  if (isCustomAppTile(t)) {
    items.push({ key: 'edit', action: 'edit', label: 'shell.launcherTileMenu.edit' })
  }
  const widgetId = dashboardWidgetForRoute(t.route)
  if (widgetId) {
    // ONE question: is the widget on the board now? A workspace panel set
    // decides only the ids it names, and a person's add always wins over it —
    // their saved choice sits on top of the panel base, on every workspace
    // kind (DESIGN-WL-HOME-DEFAULTS-ALL-WORKSPACES-1; the "a panel set
    // overrides the user's choices on every resync" premise this row used to
    // read has been false since 2026-08-01). So the row reads the board's
    // composed hidden map — `!!hidden[id]`, an ABSENT key being a
    // default-visible widget — and, before the board resolves, is offered: the
    // add path's own duplicate check answers at the grid.
    const hiddenMap = root._homeHiddenWidgets
    const alreadyOnDashboard =
      !!hiddenMap && typeof hiddenMap === 'object' && !hiddenMap[widgetId]
    if (!alreadyOnDashboard) {
      items.push({
        key: 'adddash',
        action: 'adddash',
        label: 'shell.launcherTileMenu.addToDashboard'
      })
    }
  }
  // Pin/unpin on the header tab strip (2026-08-06 per Nika "add more related
  // options to context menu to items in searchdropdown" + the drag-to-navbar
  // twin). A pin on a route the base navbar already shows is harmless —
  // applyNavbarTabPrefs filters custom rows whose path is in the base set.
  const navPath = String(t.route || '').split(/[?#]/)[0]
  if (navPath) {
    const pinned = shellNavbarTabPrefs(root).pinnedCustom.some((c) => c && c.path === navPath)
    items.push({
      key: 'navpin',
      action: 'navpin',
      label: pinned ? 'shell.launcherTileMenu.unpin' : 'shell.launcherTileMenu.pin'
    })
  }
  return items
}

// actionFn — `el.call('searchAppTileMenuAction', action)` from
// ContextDropdownItem.onClick. Owns the close.
export const searchAppTileMenuAction = function (action) {
  const root = this.state.root
  const t = root.contextDropdown?.appTile || null
  root.update({ contextDropdown: null }, { preventFetch: true })
  if (!t) return

  if (action === 'open') {
    this.call('closeDropdown', 'searchApps')
    this.call('selectAppFromSwitcher', t.route)
    return
  }

  if (action === 'sideview') {
    this.call('closeDropdown', 'searchApps')
    try {
      this.call('openAppPage', t.route, { preferSlot: true, split: true })
    } catch (_) {
      this.router(t.route, this.getRoot())
    }
    return
  }

  if (action === 'edit') {
    // Identity: the stamped projectId, else the `/x/<key>` route segment (a
    // tile composed before the stamp landed). Both are what /add-app keys its
    // thread on via `_addAppProjectId` — see state-app.js "one extension = one
    // thread". Setting it BEFORE the route means a fresh /add-app mount reads
    // the right extension on first render instead of opening a blank thread.
    const projectId =
      t.projectId ||
      String(t.route || '')
        .replace(/^\/x\//, '')
        .split(/[?#/]/)[0] ||
      ''
    if (!projectId) return
    this.call('closeDropdown', 'searchApps')
    root.update({ _addAppProjectId: projectId }, { preventFetch: true })
    this.call('openAppPage', '/add-app')
    return
  }

  if (action === 'navpin') {
    const navPath = String(t.route || '').split(/[?#]/)[0]
    if (navPath) {
      shellToggleCustomNavbarPin(root, { path: navPath, label: t.title || navPath })
    }
    return
  }

  if (action === 'adddash') {
    this.call('addLauncherAppToDashboard', t.route)
  }
}
